import { cn } from "@/lib/utils";
import { EmptyState, Icon, PageSection } from "@/components/ds";
import { Download } from "@/components/ds/icons";
import type { Pregunta } from "@/types/capacitaciones-ia";

interface CuestionarioCongeladoProps {
  preguntas: Pregunta[];
  /** PDF del respaldo firmado — `null` mientras la capacitación no tiene uno
   * generado todavía (no debería pasar en una temática ya enviada, pero el
   * botón se oculta igual en vez de ofrecer un enlace roto). */
  pdfUrl: string | null;
}

/**
 * Cuestionario congelado tal como se envió — WU6b (sdd/capacitaciones-
 * pantalla-unica). La alternativa correcta se marca comparando por ÍNDICE
 * (`j === p.respuesta_correcta_idx`), no por texto — remediación post-verify
 * #1655: comparar por texto (`op === p.respuesta_correcta`) marca AMBAS
 * alternativas cuando dos comparten el mismo literal. `p.respuesta_correcta`
 * (texto ya resuelto) no se usa acá para decidir el marcado, sólo viaja para
 * exhibición/exportación en otros consumidores.
 *
 * Índice que no resuelve (fuera de rango, o pregunta sin alternativa
 * correcta conocida): `respuesta_correcta_idx` llega `null` y ninguna
 * alternativa se marca — deliberado, con un aviso visible en vez de un
 * listado sin resaltar indistinguible de "todavía no se revisó".
 *
 * Sólo se monta en `/me/capacitaciones-ia/capacitaciones/{id}/detalle`
 * (tenant-only, auth:sanctum + EmpresaContext). El trabajador entra por
 * `/cuestionario/{token}` (público), que sigue sin mostrar la respuesta
 * correcta — invariante de `Web/CuestionarioController`, no tocado acá.
 */
export function CuestionarioCongelado({ preguntas, pdfUrl }: CuestionarioCongeladoProps) {
  return (
    <PageSection
      titulo="Cuestionario congelado"
      // El conteo va DENTRO de la descripción, no como segundo elemento en
      // `accion` — `PageSection` no envuelve su cabecera (`flex` sin
      // `flex-wrap`), así que un `accion` con dos elementos angostos el
      // título hasta partir la palabra a los 390px (verificado por CDP).
      descripcion={`Generado con IA cuando se envió la capacitación · ${preguntas.length} ${preguntas.length === 1 ? "pregunta" : "preguntas"}. No se edita.`}
      accion={
        pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="border-input hover:bg-border-faint flex h-[31px] items-center gap-1.5 rounded-md border px-2.5 text-[12px] font-medium whitespace-nowrap transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            Descargar evidencia
          </a>
        ) : undefined
      }
    >
      {preguntas.length === 0 ? (
        <EmptyState mensaje="Este cuestionario no tiene preguntas registradas." />
      ) : (
        preguntas.map((p, i) => (
          <div
            key={i}
            className="border-border-faint flex gap-3 border-b px-4 py-3.5 last:border-b-0"
          >
            <span className="fc-num text-soft shrink-0 pt-0.5 text-[11px]">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <span className="text-[12.5px] leading-[1.45] font-medium">{p.texto}</span>
              {p.opciones && (
                <div className="flex flex-col gap-1.5">
                  {p.respuesta_correcta_idx === null && (
                    <div className="text-warning flex items-center gap-1.5 text-[11px]">
                      <Icon name="warning" size={14} className="shrink-0" />
                      <span>No se pudo determinar la alternativa correcta.</span>
                    </div>
                  )}
                  {p.opciones.map((op, j) => {
                    const esCorrecta = j === p.respuesta_correcta_idx;
                    return (
                      <div
                        key={j}
                        className={cn(
                          "flex items-center gap-2 rounded-lg border px-2.5 py-1.5",
                          esCorrecta ? "border-success/30 bg-success-bg" : "border-border-faint"
                        )}
                      >
                        <Icon
                          name={esCorrecta ? "check_circle" : "radio_button_unchecked"}
                          size={16}
                          className={cn("shrink-0", esCorrecta ? "text-success" : "text-soft")}
                        />
                        <span
                          className={cn(
                            "flex-1 text-[12px] leading-[1.45]",
                            esCorrecta ? "text-success" : "text-foreground"
                          )}
                        >
                          {op}
                        </span>
                        {esCorrecta && (
                          <span className="text-success shrink-0 text-[10.5px] font-medium whitespace-nowrap">
                            Correcta
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </PageSection>
  );
}
